import { useQueryClient } from '@tanstack/react-query';
import { Archive, Lock, RefreshCw, Scissors, ShieldCheck } from 'lucide-react';
import { useState } from 'react';
import { useAuth } from '../components/AuthProvider';
import { adminOpsKeys } from '../hooks/useAdminOps';
import { AdminGroup, AuditRetentionTab } from './auditRetention.logic';
import LegalHoldsTab from './auditRetention/LegalHoldsTab';
import PruneApprovalsTab from './auditRetention/PruneApprovalsTab';

const TABS = [
  { id: AuditRetentionTab.LEGAL_HOLDS, label: 'Legal holds', icon: Lock },
  { id: AuditRetentionTab.PRUNE_APPROVALS, label: 'Prune approvals', icon: Scissors },
] as const;

const AUDIT_RETENTION_QUEUES = ['audit-legal-holds', 'audit-prune-approvals'] as const;

export default function AuditRetention() {
  const [tab, setTab] = useState<AuditRetentionTab>(AuditRetentionTab.LEGAL_HOLDS);
  const [refreshing, setRefreshing] = useState(false);
  const { hasRole } = useAuth();
  const canWrite = hasRole(AdminGroup.ADMIN);
  const queryClient = useQueryClient();

  const refresh = async () => {
    setRefreshing(true);
    await Promise.all(
      AUDIT_RETENTION_QUEUES.map(name =>
        queryClient.invalidateQueries({ queryKey: adminOpsKeys.queue(name) })
      )
    );
    setRefreshing(false);
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-3">
          <Archive className="w-7 h-7 text-primary" />
          Audit retention
        </h1>
        <p className="text-gray-text">
          Legal holds that keep audit-ledger partitions from being pruned, and the prune requests
          the retention workflow is waiting on.
        </p>
        <p className="text-xs text-gray-text mt-2 flex items-center gap-1.5">
          <ShieldCheck className="w-3.5 h-3.5" />
          {canWrite
            ? 'Every change here is dual-control: a second admin approves it in Approvals.'
            : 'Read only: placing, extending or releasing holds and deciding prunes needs the admin role.'}
        </p>
      </div>

      <div className="glass-card p-2 flex items-center gap-1 flex-wrap" role="tablist">
        {TABS.map(entry => (
          <button
            key={entry.id}
            role="tab"
            aria-selected={tab === entry.id}
            onClick={() => setTab(entry.id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
              tab === entry.id ? 'bg-primary/20 text-primary' : 'text-gray-text hover:text-white'
            }`}
          >
            <entry.icon className="w-4 h-4" />
            {entry.label}
          </button>
        ))}
        <button
          onClick={refresh}
          disabled={refreshing}
          className="ml-auto p-2 text-gray-text hover:text-white"
          aria-label="Refresh"
          title="Refresh"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {tab === AuditRetentionTab.LEGAL_HOLDS ? (
        <LegalHoldsTab canWrite={canWrite} />
      ) : (
        <PruneApprovalsTab canWrite={canWrite} />
      )}
    </div>
  );
}
